import { useMemo } from 'react';
import type { TreatmentPlan } from '../../../lib/localDB';
import { STEP_DEFINITIONS } from '../../appointments/types';

type DayEvent = {
  planId: string;
  patientName: string;
  clinic?: string;
  stepId: string;
  stepLabel: string;
  stepColor: string;
  stepIcon: string;
  operationDate: string;
  completed: boolean;
};

type Props = {
  year: number;
  month: number; // 0-based
  plans: TreatmentPlan[];
  clinics: string[]; // selected clinics (empty = all)
  surgeries: { id: string; clinic?: string }[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  today: string;
};

export type { DayEvent };

export function buildDayEvents(
  plans: TreatmentPlan[],
  surgeries: { id: string; clinic?: string }[],
  selectedClinics: string[]
): Map<string, DayEvent[]> {
  const clinicBySurgery = new Map(surgeries.map((s) => [s.id, s.clinic ?? '']));
  const map = new Map<string, DayEvent[]>();

  for (const plan of plans) {
    const clinic = clinicBySurgery.get(plan.surgery_id) ?? '';
    if (selectedClinics.length > 0 && !selectedClinics.includes(clinic)) continue;

    for (const step of plan.steps) {
      if (!step.planned_date) continue;
      const def = STEP_DEFINITIONS.find((d) => d.step_id === step.step_id);
      const event: DayEvent = {
        planId: plan.id,
        patientName: plan.patient_name,
        clinic,
        stepId: step.step_id,
        stepLabel: def?.label ?? step.label,
        stepColor: def?.color ?? 'teal',
        stepIcon: def?.icon ?? 'ri-calendar-line',
        operationDate: plan.operation_date,
        completed: step.completed,
      };
      const date = step.planned_date;
      if (!map.has(date)) map.set(date, []);
      map.get(date)!.push(event);
    }
  }

  return map;
}

const COLOR_DOT: Record<string, string> = {
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  orange: 'bg-orange-500',
  green: 'bg-green-500',
};

const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

export default function CalendarGrid({
  year, month, plans, clinics, surgeries, selectedDate, onSelectDate, today,
}: Props) {
  const dayEventsMap = useMemo(
    () => buildDayEvents(plans, surgeries, clinics),
    [plans, surgeries, clinics]
  );

  // First day of month (0=Sunday...6=Saturday -> convert to Mon=0...Sun=6)
  const firstDayOfMonth = new Date(year, month, 1);
  const rawDay = firstDayOfMonth.getDay(); // 0=Sun
  const startOffset = rawDay === 0 ? 6 : rawDay - 1; // Mon-based offset
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Month title */}
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="text-lg font-bold text-gray-800">
          {MONTHS_TR[month]} {year}
        </h3>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {DAYS_TR.map((d) => (
          <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          if (!day) {
            return (
              <div
                key={`empty-${idx}`}
                className="min-h-[90px] bg-gray-50/40 border-r border-b border-gray-100"
              />
            );
          }

          const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDate;
          const events = dayEventsMap.get(dateStr) ?? [];
          const activeEvents = events.filter((e) => !e.completed);
          const completedEvents = events.filter((e) => e.completed);

          // Group colors for dots
          const colorGroups = new Map<string, number>();
          activeEvents.forEach((e) => {
            colorGroups.set(e.stepColor, (colorGroups.get(e.stepColor) ?? 0) + 1);
          });
          const dotColors = Array.from(colorGroups.keys()).slice(0, 4);

          return (
            <div
              key={dateStr}
              onClick={() => onSelectDate(dateStr)}
              className={`min-h-[90px] p-2 border-r border-b border-gray-100 cursor-pointer transition-all
                ${isSelected ? 'bg-teal-50 ring-2 ring-inset ring-teal-400' : 'hover:bg-gray-50'}
                ${idx % 7 === 6 ? 'border-r-0' : ''}
              `}
            >
              {/* Day number */}
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold transition-colors
                    ${isToday ? 'bg-teal-500 text-white' : isSelected ? 'text-teal-700' : 'text-gray-700'}
                  `}
                >
                  {day}
                </span>
                {events.length > 0 && (
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                    activeEvents.length > 0 ? 'bg-teal-100 text-teal-700' : 'bg-green-100 text-green-600'
                  }`}>
                    {events.length}
                  </span>
                )}
              </div>

              {/* Colored dots */}
              {dotColors.length > 0 && (
                <div className="flex gap-1 flex-wrap mb-1">
                  {dotColors.map((color) => (
                    <span
                      key={color}
                      className={`w-2 h-2 rounded-full ${COLOR_DOT[color] ?? 'bg-gray-400'}`}
                    />
                  ))}
                </div>
              )}

              {/* Preview labels — max 2 */}
              <div className="space-y-0.5">
                {activeEvents.slice(0, 2).map((e, i) => (
                  <div
                    key={i}
                    className="text-xs truncate text-gray-600 font-medium leading-tight"
                    title={`${e.patientName} — ${e.stepLabel}`}
                  >
                    {e.patientName.split(' ')[0]}
                  </div>
                ))}
                {activeEvents.length > 2 && (
                  <div className="text-xs text-teal-600 font-semibold">
                    +{activeEvents.length - 2} daha
                  </div>
                )}
                {activeEvents.length === 0 && completedEvents.length > 0 && (
                  <div className="text-xs text-green-500 font-medium flex items-center gap-0.5">
                    <i className="ri-check-line text-xs"></i>
                    {completedEvents.length} tamam
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
